<template>
    <div v-clickaway="close" :class="inputClass">
        <select
            ref="select"
            :value="prefill || value"
            :autofocus="hasAutofocus"
            :required="isRequired"
            :disabled="isDisabled"
            @keydown.up.prevent="handleUpArrow()"
            @keydown.down.prevent="handleDownArrow()"
            @keydown.enter.prevent="handleEnter()"
            @keydown.esc.prevent="close()"
            @keydown.space.prevent="toggle()"
            @click.prevent="toggle()"
            @blur="blur($event)"
            @focus="focus()"
            @change="select($event)"
        >
            <option v-if="isNullable"></option>

            <template v-if="groupOptions">
                <optgroup v-for="(group, index) in optionsFiltered" :key="`optgtp-${index}`" :label="group.label">
                    <option v-for="(option, subIndex) in group.options" :key="subIndex" :value="option.value">
                        {{ option.label }}
                    </option>
                </optgroup>
            </template>

            <template v-else>
                <option v-for="(option, index) in optionsFiltered" :key="index" :value="option.value">
                    {{ option.label }}
                </option>
            </template>
        </select>

        <div class="form-input-select__wrapper">
            <div :class="valueClass" @click="click()">
                <div class="form-input-select__icon-label">
                    <common-icon v-if="icon" :name="icon" size="large"></common-icon>

                    <common-labels
                        v-if="areExpandableOptions && value"
                        class="form-input-select__common-labels"
                        :labels="selectedOptions"
                        @click="removeOption()"
                    ></common-labels>
                    <span v-else-if="!isSearchable || value" class="form-input-select__label" :title="inputLabel">
                        {{ inputLabel }}
                    </span>

                    <input
                        v-if="isSearchable && !value"
                        v-model="searchTerm"
                        class="form-input-select__search-box"
                        type="text"
                        :placeholder="placeholder"
                    />
                </div>
                <common-icon v-if="!isSearchable" name="dropdown-arrows" size="medium"></common-icon>
            </div>

            <div v-if="optionsFiltered.length" ref="options" :class="optionsClass" @mouseleave="hoverOption(-1)">
                <template v-if="groupOptions">
                    <section v-for="(group, index) in optionsFiltered" :key="index">
                        <h5
                            v-if="group.heading"
                            class="form-input-select__options-heading"
                            :class="{ 'form-input-select__options-heading--is-open': isExpanded(group) }"
                            @mouseover="hoverOption(null)"
                            @click="toggleExpand(group)"
                        >
                            {{ group.heading }}

                            <common-icon name="menu-arrow" size="large"></common-icon>
                        </h5>

                        <button
                            v-for="(option, subIndex) in group.options"
                            v-show="isExpanded(group) || group.heading === null"
                            :key="`sub-${subIndex}`"
                            ref="option"
                            :class="classOption(option, subIndex, index, isExpanded(group) && group.heading !== null)"
                            type="button"
                            tabindex="-1"
                            @mouseover="hoverOption(subIndex, index)"
                            @click="select(option.value)"
                        >
                            <checkbox-input
                                v-if="areExpandableOptions"
                                :label="option.label"
                                :hasBigTapArea="true"
                                :value="option.value === value"
                            ></checkbox-input>

                            <span v-else :title="option.label">{{ option.label }}</span>

                            <common-icon
                                v-if="option.value === value && !areExpandableOptions"
                                name="dropdown-check"
                                size="small"
                            ></common-icon>
                        </button>
                    </section>
                </template>

                <template v-else>
                    <button
                        v-for="(option, index) in optionsFiltered"
                        :key="index"
                        ref="option"
                        :class="classOption(option, index)"
                        type="button"
                        tabindex="-1"
                        @mouseover="hoverOption(index)"
                        @click="select(option.value)"
                    >
                        <span :title="option.label">{{ option.label }}</span>
                        <common-icon v-if="option.value === value" name="dropdown-check" size="small"></common-icon>
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import FormInputMixin from 'mixins/FormInputMixin'
    import IndicateScrollMixin from 'mixins/IndicateScrollMixin'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'

    export default {
        mixins: [FormInputMixin, IndicateScrollMixin],

        components: {
            CheckboxInput,
        },

        props: {
            placeholder: { type: String, default: undefined },
            value: { type: [Number, String, Object], default: undefined },
            prefill: { type: [String, Number], default: undefined },
            options: { type: Array, required: true },
            isNullable: { type: Boolean, default: false },
            groupOptions: { type: Boolean, default: false },
            expandOptionsLeft: { type: Boolean, default: false },
            expandOptionsRight: { type: Boolean, default: false },
            icon: { type: String, default: undefined },
            isSearchable: { type: Boolean, default: false },
            isSuggested: { type: Boolean, default: false },
        },

        data() {
            return {
                isOpen: false,
                isFocused: false,
                highlightedGroup: null,
                highlightedOption: null,
                expandedGroups: [],
                searchTerm: '',
            }
        },

        computed: {
            inputClass() {
                return {
                    'form-input-select': true,
                    'form-input-select--is-open': this.isOpen,
                    'form-input-select--is-focused': this.isFocused && !this.isSearchable,
                    'form-input-select--is-searchable': this.isSearchable && this.isOpen,
                    'form-input-select--is-invalid': this.isInvalid,
                    'form-input-select--is-disabled': this.isDisabled,
                    'form-input-select--is-prefilled': Boolean(this.prefill),
                    'form-input-select--is-suggested': this.isSuggested,
                }
            },

            valueClass() {
                return {
                    'form-input-select__input': true,
                    'form-input-select__input--searchable': this.isSearchable,
                    'form-input-select__input--has-no-value': !this.value,
                }
            },

            inputLabel() {
                return this.value ? this.selectedOption.label : this.placeholder
            },

            selectedOption() {
                if (this.groupOptions) {
                    const { groupIndex, optionIndex } = this.selectedGroupAndOptionIndexes()

                    return groupIndex !== null && optionIndex !== null
                        ? this.optionsFiltered[groupIndex].options[optionIndex]
                        : {}
                }

                return this.selectedIndex > -1 ? this.optionsFiltered[this.selectedIndex] : {}
            },

            selectedIndex() {
                return this.optionsFiltered.findIndex(({ value }) => JSON.stringify(this.value) === JSON.stringify(value))
            },

            hasOptions() {
                return this.$refs.option && this.$refs.option.length
            },

            optionsClass() {
                return {
                    'form-input-select__options': true,
                    'form-input-select__options-expanded': this.areExpandableOptions,
                    'form-input-select__options-right': this.expandOptionsRight,
                }
            },

            areExpandableOptions() {
                return this.expandOptionsLeft || this.expandOptionsRight
            },

            selectedOptions() {
                return (
                    this.optionsFiltered
                        .reduce((options, group) => options.concat(group.options), [])
                        .filter(({ value }) => this.selectedOption.value === value) || []
                )
            },

            optionsFiltered() {
                return this.options.filter((option) =>
                    option.label.toString().toLowerCase().includes(this.searchTerm.trim().toLowerCase()),
                )
            },
        },

        methods: {
            classOption(option, optionIndex, groupIndex = null, isOpen = false) {
                return {
                    'form-input-select__option': true,
                    'form-input-select__option--is-highlighted':
                        optionIndex === this.highlightedOption && groupIndex === this.highlightedGroup,
                    'form-input-select__suboption': isOpen,
                    'form-input-select__selected-option': option.value === this.value,
                }
            },

            hoverOption(index, groupIndex = null) {
                this.highlightedGroup = groupIndex
                this.highlightedOption = index
            },

            scrollToHighlightedOption() {
                if (!this.hasOptions) {
                    return
                }

                this.$refs.option[Math.max(this.highlightedOption, 0)].scrollIntoView({ block: 'nearest' })
            },

            switchHighlightedOption(direction = 1) {
                const position = this.highlightedOption + direction

                if (position < 0) {
                    this.highlightedOption = this.optionsFiltered.length - 1
                } else {
                    this.highlightedOption = position % this.optionsFiltered.length
                }

                this.scrollToHighlightedOption()
            },

            blur(event) {
                if (event.relatedTarget && !this.isDescendant(event.relatedTarget, this.$el)) {
                    this.close()
                }
            },

            focus() {
                this.isFocused = true
                this.highlightedOption = this.selectedIndex
                this.scrollToHighlightedOption()

                this.$refs.select.focus()
            },

            click() {
                if (!this.isSearchable) {
                    this.$refs.select.focus()
                }
                this.$refs.select.click()
            },

            close() {
                this.isOpen = false
                this.isFocused = false
            },

            toggle() {
                this.isOpen = !this.isOpen
            },

            select(option) {
                this.close()
                this.change(option)

                this.$refs.select.focus()
            },

            handleUpArrow() {
                if (this.isOpen) {
                    this.switchHighlightedOption(-1)
                } else {
                    this.toggle()
                }
            },

            handleDownArrow() {
                if (this.isOpen) {
                    this.switchHighlightedOption(1)
                } else {
                    this.toggle()
                }
            },

            handleEnter() {
                if (this.isOpen && this.hasOptions) {
                    this.select(this.optionsFiltered[this.highlightedOption].value)
                } else {
                    this.toggle()
                }
            },

            selectedGroupAndOptionIndexes() {
                for (let groupIndex = 0; groupIndex < this.optionsFiltered.length; groupIndex++) {
                    for (let optionIndex = 0; optionIndex < this.optionsFiltered[groupIndex].options.length; optionIndex++) {
                        if (this.value === this.optionsFiltered[groupIndex].options[optionIndex].value) {
                            return { groupIndex, optionIndex }
                        }
                    }
                }

                return {
                    groupIndex: null,
                    optionIndex: null,
                }
            },

            isExpanded(group) {
                return this.expandedGroups.includes(group.value)
            },

            toggleExpand(group) {
                if (!this.isExpanded(group)) {
                    this.expandedGroups.push(group.value)
                } else {
                    this.expandedGroups = this.expandedGroups.filter((item) => item !== group.value)
                }
            },

            removeOption() {
                this.change(null)
            },
        },
    }
</script>
